import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePrefs } from "@/lib/context";
import { useMyProperties } from "@/lib/hooks";

export function ItemsEditor({ value, onChange, currency }) {
  const { t, fmt } = usePrefs();
  const set = (i, k, v) => onChange(value.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const total = value.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  return (
    <div className="space-y-2" data-testid="items-editor">
      <div className="grid grid-cols-[1fr_60px_100px_24px] gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500"><span>{t("label")}</span><span>{t("qty")}</span><span>{t("unitPrice")}</span><span /></div>
      {value.map((it, i) => (
        <div key={i} className="grid grid-cols-[1fr_60px_100px_24px] gap-2">
          <Input className="h-9" placeholder={t("label")} value={it.label} onChange={(e) => set(i, "label", e.target.value)} data-testid={`item-label-${i}`} />
          <Input className="h-9 font-data text-xs" type="number" value={it.qty} onChange={(e) => set(i, "qty", e.target.value)} data-testid={`item-qty-${i}`} />
          <Input className="h-9 font-data text-xs" type="number" placeholder={t("unitPrice")} value={it.unit_price} onChange={(e) => set(i, "unit_price", e.target.value)} data-testid={`item-price-${i}`} />
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600"><X size={14} /></button>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => onChange([...value, { label: "", qty: 1, unit_price: "" }])} className="flex items-center gap-1 text-xs font-semibold text-[#996515]" data-testid="add-item-button"><Plus size={13} />{t("addLine")}</button>
        <span className="font-data text-sm" data-testid="items-total">{t("total")} : {fmt(total, currency)}</span>
      </div>
    </div>
  );
}

export const cleanItems = (items) => items.filter((i) => i.label && i.unit_price !== "").map((i) => ({ label: i.label, qty: Number(i.qty) || 1, unit_price: Number(i.unit_price) }));

export function PropertySelect({ value, onChange, testId = "property-select", options }) {
  const props = useMyProperties(!options);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>{(options || props).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function CurrencySelect({ value, onChange, testId = "currency-select" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="EUR">EUR</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
    </Select>
  );
}

export function SlaBadge({ tk }) {
  const { t } = usePrefs();
  if (!tk.sla_due_at || ["resolu", "cloture"].includes(tk.status)) return null;
  const ms = new Date(tk.sla_due_at) - Date.now();
  if (tk.escalated || ms < 0) return <span className="rounded-sm bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`sla-late-${tk.id}`}>{tk.escalated ? t("escalated") : t("late")}</span>;
  const h = ms / 36e5;
  const txt = h < 48 ? `${Math.max(1, Math.round(h))} h` : `${Math.round(h / 24)} j`;
  return <span className={`rounded-sm border px-1.5 py-0.5 font-data text-[10px] ${h < 6 ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-500"}`} data-testid={`sla-${tk.id}`}>{t("sla")} {txt}</span>;
}
